import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import DataTable from "../DataTable";
import PaginationControls from "../PaginationControls";
import { formatDateTime, normalizeListPayload } from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const DELIVERY_COLUMNS = [
  { key: "id", label: "Id" },
  { key: "user_email", label: "User Email" },
  { key: "delivery_channel", label: "Channel" },
  { key: "status", label: "Status" },
  { key: "dedupe_key", label: "Dedupe Key" },
  {
    key: "sent_at",
    label: "Sent At",
    render: (value) => formatDateTime(value),
  },
  {
    key: "created_at",
    label: "Created At",
    render: (value) => formatDateTime(value),
  },
  { key: "error_message", label: "Error Message" },
];

const AlertDeliveryLogsPanel = ({ ruleId }) => {
  const [query, setQuery] = useState({ limit: 200, offset: 0 });
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState({ items: [], total: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadDeliveries = useCallback(async () => {
    if (!ruleId) return;

    setLoading(true);
    setError("");
    try {
      const payload = await adminAnalyticsService.getAlertDeliveries(ruleId, query);
      setData(normalizeListPayload(payload));
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load delivery logs.");
    } finally {
      setLoading(false);
    }
  }, [ruleId, query]);

  useEffect(() => {
    setDraft({ limit: 200, offset: 0 });
    setQuery({ limit: 200, offset: 0 });
  }, [ruleId]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const disableNext = useMemo(
    () => data.items.length < (query.limit || 1),
    [data.items.length, query.limit]
  );

  if (!ruleId) {
    return (
      <div className="rounded-[32px] border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
        Select an alert rule to view delivery logs.
      </div>
    );
  }

  return (
    <div className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <div>
        <h3 className="text-2xl font-semibold text-slate-900">Delivery Logs</h3>
        <p className="mt-1 text-sm text-slate-500">
          Recent executions and send outcomes for the selected alert rule.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(draft);
        }}
        className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PaginationControls
            limit={draft.limit}
            offset={draft.offset}
            onLimitChange={(value) =>
              setDraft((prev) => ({ ...prev, limit: Math.max(1, value || 1) }))
            }
            onOffsetChange={(value) =>
              setDraft((prev) => ({ ...prev, offset: Math.max(0, value || 0) }))
            }
            disabled={loading}
            disableNext={disableNext}
          />
          <button
            type="submit"
            className="rounded-xl bg-[#54B0AF] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            disabled={loading}
          >
            Apply
          </button>
        </div>
      </form>

      {loading && <SectionLoading text="Loading delivery logs..." />}
      {error && <SectionError message={error} />}
      {!loading && !error && (
        <DataTable
          rows={data.items}
          columns={DELIVERY_COLUMNS}
          emptyText="No deliveries found for this rule."
        />
      )}
    </div>
  );
};

export default AlertDeliveryLogsPanel;

